import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, Injectable, Type } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StandaloneService {
  private currentComponentRef: ComponentRef<any> | null = null;
  private outlet: HTMLElement | null = null;

  constructor(
    private router: Router,
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector
  ) {
    // Get the standalone outlet element
    this.outlet = document.getElementById('standalone-outlet');

    // Check if current URL is for a standalone route
    this.checkIfStandaloneRoute();

    // Listen for navigation events
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkIfStandaloneRoute();
    });
  }

  /**
   * Check if the current URL path corresponds to a standalone route
   */
  private checkIfStandaloneRoute(): void {
    const currentRoute = this.router.routerState.root;
    this.findStandaloneRoute(currentRoute);
  }

  private findStandaloneRoute(route: any): void {
    if (route.firstChild) {
      this.findStandaloneRoute(route.firstChild);
      return;
    }

    const isStandalone = route.snapshot.data['standalone'] === true;

    if (isStandalone) {
      // Apply standalone mode styles to body
      document.body.classList.add('standalone-mode');

      // Route is marked as standalone, render it in the standalone outlet
      this.loadComponent(route.component);
    } else {
      // Not a standalone route, clean up any previously rendered component
      document.body.classList.remove('standalone-mode');
      this.clearStandaloneOutlet();
    }
  }

  private loadComponent(componentType: Type<any>): void {
    if (!this.outlet) {
      console.error('Standalone outlet not found in DOM');
      return;
    }

    // Clear any existing component
    this.clearStandaloneOutlet();

    // Create the component
    this.currentComponentRef = createComponent(componentType, {
      environmentInjector: this.injector,
      hostElement: this.outlet
    });

    // Attach to change detection
    this.appRef.attachView(this.currentComponentRef.hostView);
  }

  private clearStandaloneOutlet(): void {
    if (this.currentComponentRef) {
      this.appRef.detachView(this.currentComponentRef.hostView);
      this.currentComponentRef.destroy();
      this.currentComponentRef = null;

      if (this.outlet) {
        this.outlet.innerHTML = '';
      }
    }
  }

  // Method to programmatically navigate to a standalone route
  navigateToStandalone(route: string): void {
    this.router.navigate([route]);
  }

  // Method to open a standalone route in a new tab
  openStandaloneInNewTab(route: string): void {
    const url = window.location.origin + '/' + route;
    window.open(url, '_blank');
  }
} 
